// src/storage/chatStorage.ts
import { ChatMessage } from '../schemas/chat';

const DB_NAME = 'neverend-chat-store';
const DB_VERSION = 1;
const STORE_NAME = 'messages';

let dbInstance: IDBDatabase | null = null;

/**
 * Initialize IndexedDB database
 * Returns cached instance if already opened
 */
async function initDB(): Promise<IDBDatabase> {
  if (dbInstance) {
    return dbInstance;
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.warn('[ChatStorage] Failed to open database:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = event => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object store with conversationId as key
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'conversationId' });
      }
    };
  });
}

/**
 * Save messages for a conversation to IndexedDB
 */
export async function saveMessages(conversationId: string, messages: ChatMessage[]): Promise<void> {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const record = {
      conversationId,
      messages,
      updatedAt: new Date().toISOString(),
    };

    store.put(record);

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        console.debug(`[ChatStorage] Saved ${messages.length} messages for ${conversationId}`);
        resolve();
      };

      transaction.onerror = () => {
        console.warn('[ChatStorage] Failed to save messages:', transaction.error);
        reject(transaction.error);
      };
    });
  } catch (error) {
    // Graceful degradation: log but don't throw
    if (error instanceof Error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('[ChatStorage] Quota exceeded, caching disabled');
      } else {
        console.warn('[ChatStorage] Error saving messages:', error.message);
      }
    }
    // Silently fail - app continues without cache
  }
}

/**
 * Retrieve messages for a conversation from IndexedDB
 * Returns null if not found or on error
 */
export async function getMessages(conversationId: string): Promise<ChatMessage[] | null> {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(conversationId);

    return new Promise(resolve => {
      request.onsuccess = () => {
        const result = request.result;
        if (result && result.messages) {
          console.debug(
            `[ChatStorage] Loaded ${result.messages.length} cached messages for ${conversationId}`
          );
          resolve(result.messages);
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        console.warn('[ChatStorage] Failed to get messages:', request.error);
        resolve(null); // Trigger API fallback
      };
    });
  } catch (error) {
    console.warn('[ChatStorage] Error getting messages:', error);
    return null; // Trigger API fallback
  }
}

/**
 * Delete messages for a conversation from IndexedDB
 */
export async function deleteMessages(conversationId: string): Promise<void> {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.delete(conversationId);

    return new Promise(resolve => {
      transaction.oncomplete = () => {
        console.debug(`[ChatStorage] Deleted cached messages for ${conversationId}`);
        resolve();
      };

      transaction.onerror = () => {
        console.warn('[ChatStorage] Failed to delete messages:', transaction.error);
        resolve(); // Don't throw, cleanup failed but not critical
      };
    });
  } catch (error) {
    console.warn('[ChatStorage] Error deleting messages:', error);
    // Silently fail
  }
}

/**
 * Clear all cached messages from IndexedDB
 * Useful for logout or manual cache clearing
 */
export async function clearAllMessages(): Promise<void> {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.clear();

    return new Promise(resolve => {
      transaction.oncomplete = () => {
        console.debug('[ChatStorage] Cleared all cached messages');
        resolve();
      };

      transaction.onerror = () => {
        console.warn('[ChatStorage] Failed to clear messages:', transaction.error);
        resolve();
      };
    });
  } catch (error) {
    console.warn('[ChatStorage] Error clearing messages:', error);
  }
}

/**
 * Close the database connection
 * Call this when app shuts down or user logs out
 */
export async function closeDB(): Promise<void> {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
    console.debug('[ChatStorage] Database closed');
  }
}
